import { useState } from 'react';
import { API_BASE, telechargerFichier } from '../api/fichiers.ts';
import type { Periode } from './Accueil.tsx';

async function telecharger(url: string, nomFichier: string, setErreur: (m: string | null) => void, setEnCours: (b: boolean) => void) {
  setErreur(null);
  setEnCours(true);
  try {
    await telechargerFichier(url, nomFichier);
  } catch (err: any) {
    setErreur(err.message ?? 'Échec du téléchargement. Vérifiez votre connexion et réessayez.');
  } finally {
    setEnCours(false);
  }
}

type Cible = string;
const TABLEAUX: Array<[string, string]> = [
  ['pev', 'PEV — vaccination (T14)'], ['intrants', 'Intrants (T18)'], ['finance', 'Gestion financière (T19)'], ['deces', 'Causes de décès (T20)'],
  ['communication', 'Communication (T3)'], ['inventaire', 'Inventaire'], ['preinventaire', 'Pré-inventaire'],
];

export default function Export({ periode }: { periode: Periode | null }) {
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<Cible | null>(null);

  if (!periode) return <p>Sélectionnez d'abord une période sur l'écran Accueil.</p>;

  const suffixe = `${periode.mois}-${periode.annee}`;

  async function lancer(cible: Cible, chemin: string, nomFichier: string) {
    setEnCours(cible);
    await telecharger(`${API_BASE}/export/${periode!.id}/${chemin}`, nomFichier, setErreur, () => setEnCours(null));
  }

  return (
    <div>
      <div className="carte principale" style={{ marginBottom: 20 }}>
        <h2>RMA complet <span style={{ fontWeight: 400, fontSize: '0.8rem' }}>(modèle officiel, tous les tableaux)</span></h2>
        <button disabled={enCours !== null} onClick={() => lancer('complet', 'rma-complet', `RMA_complet_${suffixe}.xlsx`)}>
          {enCours === 'complet' ? 'Génération…' : 'Télécharger le RMA complet (.xlsx)'}
        </button>{' '}
        <button className="secondaire" disabled={enCours !== null} onClick={() => lancer('complet-pdf', 'rma-complet-pdf', `RMA_complet_${suffixe}.pdf`)}>
          {enCours === 'complet-pdf' ? 'Génération…' : 'RMA complet (.pdf)'}
        </button>
      </div>

      <div className="carte" style={{ marginBottom: 20 }}>
        <h2>Un tableau à la fois <span style={{ fontWeight: 400, fontSize: '0.8rem' }}>(CSV, Excel ou PDF ; Inventaire et Pré-inventaire : modèles papier A4)</span></h2>
        <div className="grille-export">
          {TABLEAUX.map(([code, label]) => (
            <div key={code} className="ligne-export">
              <span>{label}</span>
              <button className="secondaire" disabled={enCours !== null} onClick={() => lancer(`${code}-csv`, `tableau/${code}?format=csv`, `${code}_${suffixe}.csv`)}>CSV</button>
              <button className="secondaire" disabled={enCours !== null} onClick={() => lancer(`${code}-xlsx`, `tableau/${code}?format=xlsx`, `${code}_${suffixe}.xlsx`)}>Excel</button>
              <button className="secondaire" disabled={enCours !== null} onClick={() => lancer(`${code}-pdf`, `tableau/${code}?format=pdf`, `${code}_${suffixe}.pdf`)}>PDF</button>
            </div>
          ))}
        </div>
      </div>

      <div className="carte" style={{ marginBottom: 20 }}>
        <h2>RCE / Registre CRENAS <span style={{ fontWeight: 400, fontSize: '0.8rem' }}>(Tableaux 4, 6, 8, 11)</span></h2>
        <p style={{ fontSize: '0.85rem', color: '#666' }}>
          Reprend exactement les données et calculs des écrans Saisie unique, RMA et Tableau de suivi.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 320 }}>
          <button disabled={enCours !== null} onClick={() => lancer('rce-excel', 'excel', `PRE_RMA_RCE_${suffixe}.xlsx`)}>
            {enCours === 'rce-excel' ? 'Génération…' : 'Exporter en Excel (.xlsx)'}
          </button>
          <button className="secondaire" disabled={enCours !== null} onClick={() => lancer('rce-pdf', 'pdf', `PRE_RMA_RCE_${suffixe}.pdf`)}>
            {enCours === 'rce-pdf' ? 'Génération…' : 'Exporter en PDF (A4)'}
          </button>
        </div>
      </div>

      <div className="carte">
        <h2>Saisie CPN <span style={{ fontWeight: 400, fontSize: '0.8rem' }}>(Tableaux 9, 12)</span></h2>
        <p style={{ fontSize: '0.85rem', color: '#666' }}>
          Reprend exactement les données et calculs des écrans Saisie CPN et RMA CPN.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 320 }}>
          <button disabled={enCours !== null} onClick={() => lancer('cpn-excel', 'excel-cpn', `PRE_RMA_CPN_${suffixe}.xlsx`)}>
            {enCours === 'cpn-excel' ? 'Génération…' : 'Exporter en Excel (.xlsx)'}
          </button>
          <button className="secondaire" disabled={enCours !== null} onClick={() => lancer('cpn-pdf', 'pdf-cpn', `PRE_RMA_CPN_${suffixe}.pdf`)}>
            {enCours === 'cpn-pdf' ? 'Génération…' : 'Exporter en PDF (A4)'}
          </button>
        </div>
      </div>

      {erreur && <p className="messages-erreur">{erreur}</p>}

      <p style={{ marginTop: 24, fontSize: '0.85rem', color: '#666' }}>
        L'export nécessite une connexion active (il interroge le serveur pour garantir des données à
        jour) — indisponible hors connexion, contrairement à la consultation des écrans déjà ouverts.
      </p>
    </div>
  );
}
